import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { getPublicCartTotalQuantity } from '@/utils/publicCart';
import { auth } from '@/store/authStore';
import { clubsBookingApi } from '@/api/clubs-booking';
import { getClubSiteHref, getClubSiteSlugFromHostname } from '@/utils/publicSite';

type PublicClubHeaderProps = {
  clubName: string;
  clubSlug?: string;
  clubDescription?: string;
  logoUrl?: string;
  preferSessionMonkeyFallbackLogo?: boolean;
  showClubTitle?: boolean;
  cartHref?: string;
  actions?: ReactNode;
};

const PublicClubHeader = ({
  clubName,
  clubSlug,
  logoUrl,
  preferSessionMonkeyFallbackLogo = false,
  showClubTitle = true,
  cartHref = '/cart',
  actions,
}: PublicClubHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [parentLoggedIn, setParentLoggedIn] = useState(false);
  const [parentName, setParentName] = useState('');
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [ownsCurrentClub, setOwnsCurrentClub] = useState(false);

  const activeClubSlug = useMemo(() => {
    const slugFromProp = String(clubSlug || '').trim().toLowerCase();
    if (slugFromProp) return slugFromProp;
    const slugFromHost = getClubSiteSlugFromHostname();
    return String(slugFromHost || '').trim().toLowerCase();
  }, [clubSlug]);

  useEffect(() => {
    const update = () => setCartCount(getPublicCartTotalQuantity());
    update();
    window.addEventListener('storage', update);
    window.addEventListener('focus', update);
    window.addEventListener('clubsbooking-cart-updated', update);
    return () => {
      window.removeEventListener('storage', update);
      window.removeEventListener('focus', update);
      window.removeEventListener('clubsbooking-cart-updated', update);
    };
  }, []);

  useEffect(() => {
    const updateParentAuth = () => {
      const user = auth.getUser();
      setParentLoggedIn(!!(user.isAuthenticated && user.role === 'parent'));
      setParentName(user.name || '');
      setAdminLoggedIn(!!(user.isAuthenticated && user.role !== 'parent'));
    };
    updateParentAuth();
    window.addEventListener('storage', updateParentAuth);
    window.addEventListener('focus', updateParentAuth);
    window.addEventListener('clubsbooking-auth-updated', updateParentAuth);
    return () => {
      window.removeEventListener('storage', updateParentAuth);
      window.removeEventListener('focus', updateParentAuth);
      window.removeEventListener('clubsbooking-auth-updated', updateParentAuth);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!adminLoggedIn || !activeClubSlug) {
      setOwnsCurrentClub(false);
      return;
    }

    let cancelled = false;
    const checkCurrentClubOwnership = async () => {
      try {
        const profile = await clubsBookingApi.getMyClubProfile();
        const ownSlug = String(profile?.publicSlug || '').trim().toLowerCase();
        if (!cancelled) {
          setOwnsCurrentClub(!!ownSlug && ownSlug === activeClubSlug);
        }
      } catch {
        if (!cancelled) {
          setOwnsCurrentClub(false);
        }
      }
    };

    void checkCurrentClubOwnership();

    return () => {
      cancelled = true;
    };
  }, [adminLoggedIn, activeClubSlug]);

  const resolvedCartHref = (() => {
    if (!cartHref.startsWith('/cart')) return cartHref;
    if (location.pathname === '/cart') return cartHref;
    if (cartHref.includes('backTo=')) return cartHref;
    const separator = cartHref.includes('?') ? '&' : '?';
    const backTo = `${location.pathname}${location.search}`;
    return `${cartHref}${separator}backTo=${encodeURIComponent(backTo)}`;
  })();

  const clubInitials = String(clubName || 'Club')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'CL';

  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex w-full max-w-[1320px] flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 py-4 sm:px-4 sm:py-4 md:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={clubName}
              className="h-12"
            />
          ) : preferSessionMonkeyFallbackLogo ? (
            <img
              src="/logo.png"
              alt="Session Monkey"
              className="h-12 w-12 rounded-md object-contain"
            />
          ) : (
            <div
              data-testid="public-club-header-logo-fallback"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#F6FAF7] text-sm font-semibold text-[#1F8F5F]"
              aria-label={`${clubName} initials`}
            >
              {clubInitials}
            </div>
          )}
          <div className="min-w-0">
            {showClubTitle &&
              (clubSlug ? (
                <a
                  href={getClubSiteHref(clubSlug)}
                  data-testid="public-club-header-title"
                  className="truncate text-lg font-semibold italic tracking-tight text-slate-900 hover:underline sm:text-xl"
                >
                  {clubName}
                </a>
              ) : (
                <p
                  data-testid="public-club-header-title"
                  className="truncate text-lg font-semibold italic tracking-tight text-slate-900 sm:text-xl"
                >
                  {clubName}
                </p>
              ))}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {actions}
          {ownsCurrentClub && (
            <Link
              to="/dashboard"
              data-testid="public-club-admin-dashboard-link"
              className="inline-flex items-center whitespace-nowrap rounded-lg bg-[#1F8F5F] px-3 py-2 text-sm font-semibold text-white hover:bg-[#1a7a52]"
            >
              Go to admin dashboard
            </Link>
          )}
          {parentLoggedIn ? (
            <>
              <span className="max-w-[180px] truncate rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                Parent{parentName ? `: ${parentName}` : ''}
              </span>
              <button
                type="button"
                className="text-sm text-slate-600 hover:underline"
                onClick={() => {
                  auth.clearAuth();
                  clubsBookingApi.clearAuthToken();
                  setParentLoggedIn(false);
                  navigate('/parent/login');
                }}
              >
                Logout
              </button>
            </>
          ) : adminLoggedIn ? (
            <span className="max-w-[180px] truncate rounded-full border border-[#D8F1E2] bg-[#F3FBF7] px-2.5 py-1 text-xs font-semibold text-[#1F8F5F]">
              Admin signed in
            </span>
          ) : (
            <Link to="/parent/login" className="text-sm font-medium text-slate-700 hover:underline">
              Login
            </Link>
          )}
          <Link to={resolvedCartHref} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D8F1E2] bg-[#F3FBF7] px-2.5 py-2 text-sm font-medium text-[#1F8F5F] hover:bg-[#EAF7F0] sm:gap-2 sm:px-3">
            <ShoppingCart className="h-4 w-4" />
            Cart ({cartCount})
          </Link>
        </div>
      </div>
      {ownsCurrentClub && (
        <div
          data-testid="public-club-owner-banner"
          className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs font-medium text-amber-800 sm:px-4 md:px-8"
        >
          This is your site. Only you can see this banner.
        </div>
      )}
    </header>
  );
};

export default PublicClubHeader;
